
import { useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import { useSEO, getAboutPageSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function AboutPage() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');

  // SEO Implementation
  useSEO({
    title: isNL
      ? 'Over Thyamis - Toonaangevende IT-oplossingen Aanbieder Sinds 2025'
      : 'About Thyamis - Leading IT Solutions Provider Since 2025',
    description: isNL
      ? 'Leer meer over Thyamis, een toonaangevende aanbieder van uitgebreide IT-oplossingen. Wij zijn gespecialiseerd in enterprise hardware, software, clouddiensten, cyberbeveiliging en geavanceerde AI-oplossingen voor digitale transformatie en zakelijk succes.'
      : 'Learn about Thyamis, a premier provider of comprehensive IT solutions. We specialize in enterprise hardware, software, cloud services, cybersecurity, and cutting-edge AI solutions to drive digital transformation and business success.',
    keywords: isNL
      ? 'over Thyamis, IT-bedrijf, enterprise technologie aanbieder, IT-oplossingen bedrijf, digitale transformatie, technologiepartner'
      : 'about Thyamis, IT company, enterprise technology provider, IT solutions company, digital transformation, technology partner',
    canonical: isNL ? '/nl/about' : '/about',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getAboutPageSchema(),
        getBreadcrumbSchema([
          { name: isNL ? 'Home' : 'Home', url: isNL ? '/nl' : '/' },
          { name: isNL ? 'Over Ons' : 'About', url: isNL ? '/nl/about' : '/about' },
        ]),
      ],
    },
  });

  const prefix = isNL ? '/nl' : '';

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative w-full py-20 bg-gradient-to-r from-white via-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="w-full max-w-3xl">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {isNL ? 'Over Thyamis' : 'About Thyamis'}
            </h1>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {isNL
                ? 'Pioniers in de toekomst van enterprise technologie sinds 2025. Wij zijn uw betrouwbare partner in digitale transformatie en leveren innovatieve IT-oplossingen die zakelijk succes stimuleren.'
                : 'Pioneering the future of enterprise technology since 2025. We are your trusted partner in digital transformation, delivering innovative IT solutions that drive business success.'}
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href={prefix || '/'}
                className="bg-primary text-white px-6 py-3 rounded-button whitespace-nowrap font-medium inline-flex items-center hover:bg-primary/90 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-home-line"></i>
                </div>
                {isNL ? 'Terug naar Home' : 'Back to Home'}
              </a>
              <button
                onClick={() => {
                  document.getElementById('contact-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-transparent border-2 border-primary text-primary px-6 py-3 rounded-button whitespace-nowrap font-medium hover:bg-primary/5 transition-colors cursor-pointer"
              >
                {isNL ? 'Neem Contact Op' : 'Contact Our Team'}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Company Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900">
                {isNL ? 'Leiders in de IT-revolutie' : 'Leading the IT Revolution'}
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                {isNL
                  ? 'Opgericht in 2025 heeft Thyamis zich snel gevestigd als een toonaangevende aanbieder van uitgebreide IT-oplossingen. Wij zijn gespecialiseerd in het overbruggen van de kloof tussen geavanceerde technologie en praktische bedrijfstoepassingen, en helpen organisaties van elke omvang hun doelen op het gebied van digitale transformatie te bereiken.'
                  : 'Founded in 2025, Thyamis has rapidly established itself as a premier provider of comprehensive IT solutions. We specialize in bridging the gap between cutting-edge technology and practical business applications, helping organizations of all sizes achieve their digital transformation goals.'}
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                {isNL
                  ? 'Onze toewijding aan uitmuntendheid, innovatie en klantsucces heeft ons de betrouwbare partner gemaakt voor bedrijven die op zoek zijn naar betrouwbare, schaalbare en toekomstbestendige IT-infrastructuur.'
                  : 'Our commitment to excellence, innovation, and customer success has made us the trusted partner for businesses seeking reliable, scalable, and future-ready IT infrastructure.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-12 text-gray-900">
              {isNL ? 'Onze Missie &amp; Visie' : 'Our Mission &amp; Vision'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-blue-50 p-8 rounded-lg border border-blue-100">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-8 h-8 flex items-center justify-center text-primary">
                    <i className="ri-target-line text-3xl"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">
                  {isNL ? 'Onze Missie' : 'Our Mission'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {isNL
                    ? 'Bedrijven versterken met innovatieve IT-oplossingen die groei, efficiëntie en concurrentievoordeel stimuleren. Wij zijn toegewijd aan het leveren van uitzonderlijke service en geavanceerde technologie die de manier waarop organisaties werken en slagen transformeert.'
                    : 'To empower businesses with innovative IT solutions that drive growth, efficiency, and competitive advantage. We are committed to delivering exceptional service and cutting-edge technology that transforms how organizations operate and succeed.'}
                </p>
              </div>
              <div className="bg-teal-50 p-8 rounded-lg border border-teal-100">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                    <i className="ri-eye-line text-3xl"></i>
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-900">
                  {isNL ? 'Onze Visie' : 'Our Vision'}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {isNL
                    ? 'De wereldleider zijn in enterprise IT-oplossingen, erkend om onze innovatie, betrouwbaarheid en onwankelbare toewijding aan klantsucces. Wij zien een toekomst waarin technologie naadloos integreert met bedrijfsdoelstellingen om onbeperkte mogelijkheden te creëren.'
                    : 'To be the global leader in enterprise IT solutions, recognized for our innovation, reliability, and unwavering commitment to client success. We envision a future where technology seamlessly integrates with business objectives to create unlimited possibilities.'}
                </p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-primary">
                  <i className="ri-lightbulb-line text-2xl"></i>
                </div>
              </div>
              <h4 className="font-semibold mb-2 text-gray-900">{isNL ? 'Innovatie' : 'Innovation'}</h4>
              <p className="text-gray-600 text-sm">
                {isNL
                  ? 'Voortdurend grenzen verleggen om geavanceerde oplossingen te leveren'
                  : 'Continuously pushing boundaries to deliver cutting-edge solutions'}
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-green-600">
                  <i className="ri-shield-check-line text-2xl"></i>
                </div>
              </div>
              <h4 className="font-semibold mb-2 text-gray-900">{isNL ? 'Betrouwbaarheid' : 'Reliability'}</h4>
              <p className="text-gray-600 text-sm">
                {isNL
                  ? 'Consistente, betrouwbare service leveren waar u op kunt vertrouwen'
                  : 'Delivering consistent, dependable service you can trust'}
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-orange-600">
                  <i className="ri-team-line text-2xl"></i>
                </div>
              </div>
              <h4 className="font-semibold mb-2 text-gray-900">{isNL ? 'Partnerschap' : 'Partnership'}</h4>
              <p className="text-gray-600 text-sm">
                {isNL
                  ? 'Langdurige relaties opbouwen gebaseerd op wederzijds succes'
                  : 'Building long-term relationships based on mutual success'}
              </p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center mx-auto mb-4">
                <div className="w-6 h-6 flex items-center justify-center text-yellow-600">
                  <i className="ri-star-line text-2xl"></i>
                </div>
              </div>
              <h4 className="font-semibold mb-2 text-gray-900">{isNL ? 'Uitmuntendheid' : 'Excellence'}</h4>
              <p className="text-gray-600 text-sm">
                {isNL
                  ? 'De hoogste standaarden handhaven in alles wat we doen'
                  : 'Maintaining the highest standards in everything we do'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas of Expertise Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              {isNL ? 'Onze Expertisegebieden' : 'Our Areas of Expertise'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {isNL
                ? 'Wij zijn gespecialiseerd in uitgebreide IT-oplossingen die elk aspect van uw technologische infrastructuur en bedrijfsbehoeften dekken.'
                : 'We specialize in comprehensive IT solutions that cover every aspect of your technology infrastructure and business needs.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Infrastructure Solutions */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-primary">
                  <i className="ri-server-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {isNL ? 'Infrastructuuroplossingen' : 'Infrastructure Solutions'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Enterprise-grade hardware, netwerk- en opslagoplossingen ontworpen om uw bedrijfsgroei en operationele vereisten te ondersteunen.'
                  : 'Enterprise-grade hardware, networking, and storage solutions designed to support your business growth and operational requirements.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Server- &amp; Opslagsystemen' : 'Server &amp; Storage Systems'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Netwerkarchitectuur' : 'Network Architecture'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-primary mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Datacenteroplossingen' : 'Data Center Solutions'}</span>
                </li>
              </ul>
            </div>

            {/* Cloud & Digital Transformation */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-green-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-green-600">
                  <i className="ri-cloud-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {isNL ? 'Cloud &amp; Digitale Transformatie' : 'Cloud &amp; Digital Transformation'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Uitgebreide cloudmigratie, hybride oplossingen en digitale transformatiediensten om uw bedrijfsvoering te moderniseren.'
                  : 'Comprehensive cloud migration, hybrid solutions, and digital transformation services to modernize your business operations.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Cloudmigratiestrategie' : 'Cloud Migration Strategy'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Hybride Cloudoplossingen' : 'Hybrid Cloud Solutions'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-green-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Digitale Werkstroomoptimalisatie' : 'Digital Workflow Optimization'}</span>
                </li>
              </ul>
            </div>

            {/* Artificial Intelligence */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-indigo-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-indigo-600">
                  <i className="ri-ai-generate text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {isNL ? 'Kunstmatige Intelligentie' : 'Artificial Intelligence'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Op maat gemaakte AI-ontwikkeling, machine learning-implementatie en intelligente automatiseringsoplossingen voor enterprise toepassingen.'
                  : 'Custom AI development, machine learning implementation, and intelligent automation solutions for enterprise applications.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-indigo-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Op Maat Gemaakte AI-ontwikkeling' : 'Custom AI Development'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-indigo-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Machine Learning Modellen' : 'Machine Learning Models'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-indigo-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Procesautomatisering' : 'Process Automation'}</span>
                </li>
              </ul>
            </div>

            {/* Cybersecurity */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-red-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-red-600">
                  <i className="ri-shield-check-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {isNL ? 'Cyberbeveiliging' : 'Cybersecurity'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Geavanceerde beveiligingsoplossingen om uw bedrijf te beschermen tegen evoluerende cyberdreigingen en naleving van industriestandaarden te waarborgen.'
                  : 'Advanced security solutions to protect your business from evolving cyber threats and ensure compliance with industry standards.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-red-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Dreigingsbeoordeling' : 'Threat Assessment'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-red-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Beveiligingsimplementatie' : 'Security Implementation'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-red-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? '24/7 Monitoring' : '24/7 Monitoring'}</span>
                </li>
              </ul>
            </div>

            {/* Managed Services */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-teal-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-teal-600">
                  <i className="ri-tools-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {isNL ? 'Beheerde Diensten' : 'Managed Services'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Uitgebreide IT-beheer- en ondersteuningsdiensten om uw systemen soepel en efficiënt te laten draaien.'
                  : 'Comprehensive IT management and support services to keep your systems running smoothly and efficiently.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Proactieve Monitoring' : 'Proactive Monitoring'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Helpdesk Ondersteuning' : 'Help Desk Support'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-teal-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Systeemonderhoud' : 'System Maintenance'}</span>
                </li>
              </ul>
            </div>

            {/* IT Consulting */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-100 transition-all hover:shadow-md">
              <div className="w-16 h-16 bg-orange-50 rounded-lg flex items-center justify-center mb-6">
                <div className="w-8 h-8 flex items-center justify-center text-orange-600">
                  <i className="ri-lightbulb-line text-3xl"></i>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">
                {isNL ? 'IT-advies' : 'IT Consulting'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isNL
                  ? 'Strategisch technologieadvies om u te helpen weloverwogen beslissingen te nemen en uw IT-investeringen te optimaliseren voor maximaal rendement.'
                  : 'Strategic technology consulting to help you make informed decisions and optimize your IT investments for maximum ROI.'}
              </p>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Technologiestrategie' : 'Technology Strategy'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'ROI-analyse' : 'ROI Analysis'}</span>
                </li>
                <li className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center text-orange-600 mt-0.5 mr-2">
                    <i className="ri-check-line"></i>
                  </div>
                  <span className="text-gray-700 text-sm">{isNL ? 'Implementatieplanning' : 'Implementation Planning'}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 relative" id="contact-section" style={{ background: 'linear-gradient(135deg, #0066CC 0%, #0052A3 100%)' }}>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              {isNL ? 'Klaar om samen te werken met Thyamis?' : 'Ready to Partner with Thyamis?'}
            </h2>
            <p className="text-blue-100 mb-8 text-lg">
              {isNL
                ? 'Laten we bespreken hoe onze expertise uw bedrijf kan transformeren met innovatieve IT-oplossingen op maat van uw specifieke behoeften.'
                : 'Let\u2019s discuss how our expertise can help transform your business with innovative IT solutions tailored to your specific needs.'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 flex items-center justify-center text-white">
                    <i className="ri-phone-line text-3xl"></i>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {isNL ? 'Bel Ons' : 'Call Us'}
                </h3>
                <p className="text-blue-100">+31 (0)616300011</p>
                <p className="text-blue-100 text-sm">{isNL ? '24/7 bereikbaar' : 'Available 24/7'}</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <div className="w-8 h-8 flex items-center justify-center text-white">
                    <i className="ri-mail-line text-3xl"></i>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {isNL ? 'E-mail Ons' : 'Email Us'}
                </h3>
                <p className="text-blue-100">info@thyamis.com</p>
                <p className="text-blue-100 text-sm">{isNL ? 'Reactie binnen 2 uur' : 'Response within 2 hours'}</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`${prefix}/contact`}
                className="bg-white text-primary px-8 py-3 rounded-button whitespace-nowrap font-medium hover:bg-gray-100 transition-colors cursor-pointer"
              >
                {isNL ? 'Neem Contact Op' : 'Get In Touch'}
              </a>
              <a
                href={prefix || '/'}
                className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-button whitespace-nowrap font-medium hover:bg-white/10 transition-colors cursor-pointer"
              >
                {isNL ? 'Ontdek Oplossingen' : 'Explore Solutions'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
